import { BreadcrumbHeader } from "@/components/BreadcrumbHeader";
import { CourseSkillLevelBadge } from "@/components/ui/CourseSkillLevelBadge";
import { CategoryType } from "@/lib/constants";
import { CONTENT_MAX_WIDTH } from "@/lib/useDeviceType";
import type { CollectionInfo } from "@/supabase/services/collections";
import React from "react";
import { Platform } from "react-native";
import { Image, Separator, XStack, YStack } from "tamagui";
import { BodyText, EyebrowText, PageTitle } from "@/components/ui/Typography";

type CollectionPageHeaderProps = {
  courseSlug: string;
  collection: CollectionInfo | null | undefined;
  isMobile: boolean;
};

/** Breadcrumb + collection hero block (when collection has a real id). */
export function CollectionPageHeader({
  courseSlug,
  collection,
  isMobile,
}: CollectionPageHeaderProps) {
  const hasCollectionHero = !!(collection && collection.id > 0);

  return (
    <>
      <YStack w="100%" pt="$4">
        <XStack w="100%" py="$1" ai="center" $gtSm={{ py: "$2" }}>
          <BreadcrumbHeader
            courseSlug={courseSlug}
            courseTitle={collection?.courseTitle}
            insetX={12}
          />
        </XStack>
      </YStack>

      {hasCollectionHero ? (
        <YStack
          w="100%"
          px={12}
          maw={CONTENT_MAX_WIDTH}
          mx="auto"
          ai="flex-start"
          py={14}
          $gtSm={{ py: 18 }}
          $gtMd={{ py: 20 }}
        >
          <XStack
            w="100%"
            h={64}
            ai="stretch"
            gap={16}
            $gtSm={{ h: 88, gap: 18 }}
            $gtMd={{ h: 104, gap: 20 }}
          >
            <YStack
              w={64}
              h={64}
              br={8}
              bg="#eee"
              overflow="hidden"
              flexShrink={0}
              aspectRatio={1}
              $gtSm={{ w: 88, h: 88 }}
              $gtMd={{ w: 104, h: 104 }}
            >
              {collection?.thumbnail ? (
                <Image
                  source={{ uri: collection.thumbnail }}
                  width="100%"
                  height="100%"
                  resizeMode="cover"
                />
              ) : null}
            </YStack>
            <YStack
              flex={1}
              minWidth={0}
              overflow="hidden"
              h={64}
              $gtSm={{ h: 88 }}
              $gtMd={{ h: 104 }}
              jc={
                collection?.summary?.trim() ? "space-between" : "flex-start"
              }
              ai="flex-start"
            >
              <YStack w="100%" flexShrink={0}>
                <XStack
                  w="100%"
                  ai="center"
                  jc="flex-start"
                  gap={10}
                  $gtSm={{ gap: 16 }}
                >
                  <PageTitle
                    flexGrow={0}
                    flexShrink={1}
                    minWidth={0}
                    color="#0D0D0D"
                    ta="left"
                    numberOfLines={isMobile ? 1 : 2}
                    ellipsizeMode="tail"
                  >
                    {collection?.title}
                  </PageTitle>
                  {collection?.courseSkillLevel != null ? (
                    <CourseSkillLevelBadge
                      level={collection.courseSkillLevel}
                      showLabel={!isMobile}
                    />
                  ) : null}
                </XStack>
                <EyebrowText
                  color="#987451"
                  ta="left"
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {collection?.categoryType === CategoryType.Section
                    ? "Section"
                    : "Collection"}
                </EyebrowText>
              </YStack>
              {collection?.summary?.trim() ? (
                <BodyText
                  letterSpacing={0}
                  color="#0D0D0D"
                  ta="left"
                  numberOfLines={2}
                  ellipsizeMode="tail"
                  w="100%"
                  $gtLg={{ size: "$5" }}
                  style={
                    Platform.OS === "web"
                      ? ({
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          overflow: "hidden",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        } as object)
                      : undefined
                  }
                >
                  {collection.summary.trim()}
                </BodyText>
              ) : null}
            </YStack>
          </XStack>
        </YStack>
      ) : null}
    </>
  );
}

/** Centered title + divider for special collections without a hero block. */
export function CollectionFallbackTitle({
  title,
}: {
  title?: string | null;
}) {
  return (
    <YStack als="center" w="100%" pb="$5" gap="$4">
      <PageTitle ta="center" fontWeight="500" color="#0D0D0D">
        {title}
      </PageTitle>
      <Separator width="95%" alignSelf="center" borderColor="#987451" />
    </YStack>
  );
}

export function CollectionHeaderSpacer({
  hasCollectionHero,
}: {
  hasCollectionHero: boolean;
}) {
  return <YStack h={hasCollectionHero ? 12 : 0} />;
}
